import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { FileText, Download, TrendingUp, Users, Truck } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { PageHeader } from '@/components/common/PageHeader';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { DatePicker } from '@/components/ui/date-picker';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { AreaChart } from '@/components/charts/AreaChart';
import { Spinner } from '@/components/ui/spinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { reportsApi, type ReportsQuery } from '../api/reports.api';
import toast from 'react-hot-toast';

const reportTypes = [
  {
    id: 'bookings',
    title: 'Booking Reports',
    description: 'Daily, weekly, and monthly booking analytics',
    icon: FileText,
  },
  {
    id: 'revenue',
    title: 'Revenue Reports',
    description: 'Financial performance and revenue breakdown',
    icon: TrendingUp,
  },
  {
    id: 'drivers',
    title: 'Driver Reports',
    description: 'Driver performance and activity metrics',
    icon: Truck,
  },
  {
    id: 'customers',
    title: 'Customer Reports',
    description: 'Customer acquisition and retention metrics',
    icon: Users,
  },
];

const periodOptions = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last7days', label: 'Last 7 Days' },
  { value: 'last30days', label: 'Last 30 Days' },
  { value: 'thisMonth', label: 'This Month' },
  { value: 'lastMonth', label: 'Last Month' },
  { value: 'custom', label: 'Custom Range' },
];

export function ReportsPage() {
  const [activeTab, setActiveTab] = useState('bookings');
  const [period, setPeriod] = useState('last7days');
  const [state, setState] = useState('all');
  const [fromDate, setFromDate] = useState<Date | undefined>();
  const [toDate, setToDate] = useState<Date | undefined>();

  const { data: states } = useQuery({
    queryKey: ['report-states'],
    queryFn: () => reportsApi.getStates(),
    staleTime: 5 * 60 * 1000,
  });
  const stateOptions = [
    { value: 'all', label: 'All States' },
    ...(states ?? []).map((s) => ({ value: s, label: s })),
  ];

  const query: ReportsQuery = {
    period: period !== 'custom' ? period : undefined,
    startDate: period === 'custom' && fromDate ? fromDate.toISOString().split('T')[0] : undefined,
    endDate: period === 'custom' && toDate ? toDate.toISOString().split('T')[0] : undefined,
    state: state !== 'all' ? state : undefined,
  };

  const { data: bookingReport, isLoading: bookingsLoading } = useQuery({
    queryKey: ['report-bookings', query],
    queryFn: () => reportsApi.getBookingReport(query),
    enabled: activeTab === 'bookings',
  });

  const { data: revenueReport, isLoading: revenueLoading } = useQuery({
    queryKey: ['report-revenue', query],
    queryFn: () => reportsApi.getRevenueReport(query),
    enabled: activeTab === 'revenue',
  });

  const { data: driverReport, isLoading: driversLoading } = useQuery({
    queryKey: ['report-drivers', query],
    queryFn: () => reportsApi.getDriverReport(query),
    enabled: activeTab === 'drivers',
  });

  const { data: customerReport, isLoading: customersLoading } = useQuery({
    queryKey: ['report-customers', query],
    queryFn: () => reportsApi.getCustomerReport(query),
    enabled: activeTab === 'customers',
  });

  const exportMutation = useMutation({
    mutationFn: (format: 'csv' | 'pdf') =>
      reportsApi.exportReport({ ...query, type: activeTab, format }),
    onSuccess: (blob, format) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${activeTab}-report.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Report exported as ${format.toUpperCase()}`);
    },
    onError: () => toast.error('Export failed'),
  });

  const bookingChartData = bookingReport?.chartData ?? [];
  const revenueChartData = revenueReport?.chartData ?? [];
  const bookingSummary = bookingReport?.summary;
  const revenueSummary = revenueReport?.summary;
  const topDrivers = driverReport?.topDrivers ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Reports"
        description="Generate and export detailed reports for your business"
      />

      {/* Report Type Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {reportTypes.map((report) => (
          <Card
            key={report.id}
            className={`cursor-pointer transition-colors ${
              activeTab === report.id ? 'border-primary' : 'hover:border-primary/50'
            }`}
            onClick={() => setActiveTab(report.id)}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-primary/10 p-2">
                  <report.icon className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-medium">{report.title}</h3>
                  <p className="text-xs text-muted-foreground">{report.description}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-2">
              <Label>Period</Label>
              <Select
                value={period}
                onChange={setPeriod}
                options={periodOptions}
                className="w-40"
              />
            </div>

            {period === 'custom' && (
              <>
                <div className="space-y-2">
                  <Label>From</Label>
                  <DatePicker value={fromDate} onChange={setFromDate} />
                </div>
                <div className="space-y-2">
                  <Label>To</Label>
                  <DatePicker value={toDate} onChange={setToDate} />
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label>State</Label>
              <Select
                value={state}
                onChange={setState}
                options={stateOptions}
                className="w-40"
              />
            </div>

            <div className="flex gap-2 ml-auto">
              <Button
                variant="outline"
                onClick={() => exportMutation.mutate('csv')}
                disabled={exportMutation.isPending}
              >
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
              <Button
                variant="outline"
                onClick={() => exportMutation.mutate('pdf')}
                disabled={exportMutation.isPending}
              >
                <Download className="h-4 w-4 mr-2" />
                Export PDF
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Report Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        {/* Bookings Tab */}
        <TabsContent value="bookings" className="space-y-6 mt-0">
          {bookingsLoading ? (
            <div className="flex justify-center py-12"><Spinner /></div>
          ) : (
            <div className="grid gap-6 lg:grid-cols-2">
              <AreaChart
                title="Booking Trend"
                data={bookingChartData}
                xKey="date"
                yKeys={[{ key: 'value', name: 'Bookings', color: '#3B82F6' }]}
                height={300}
              />
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Total Bookings</span>
                      <span className="font-medium">{bookingSummary?.totalBookings?.toLocaleString() ?? '—'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Completed</span>
                      <span className="font-medium text-success">{bookingSummary?.completed?.toLocaleString() ?? '—'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Cancelled</span>
                      <span className="font-medium text-destructive">{bookingSummary?.cancelled?.toLocaleString() ?? '—'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Completion Rate</span>
                      <span className="font-medium">
                        {bookingSummary?.completionRate != null ? `${bookingSummary.completionRate.toFixed(1)}%` : '—'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Avg. per Day</span>
                      <span className="font-medium">{bookingSummary?.avgPerDay?.toFixed(0) ?? '—'}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Revenue Tab */}
        <TabsContent value="revenue" className="space-y-6 mt-0">
          {revenueLoading ? (
            <div className="flex justify-center py-12"><Spinner /></div>
          ) : (
            <div className="grid gap-6 lg:grid-cols-2">
              <AreaChart
                title="Revenue Trend"
                data={revenueChartData}
                xKey="date"
                yKeys={[{ key: 'value', name: 'Revenue', color: '#10B981' }]}
                height={300}
                formatValue="currency"
              />
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Revenue Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Total Revenue</span>
                      <span className="font-medium">
                        {revenueSummary?.totalRevenue != null ? formatCurrency(revenueSummary.totalRevenue) : '—'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Platform Fee</span>
                      <span className="font-medium">
                        {revenueSummary?.platformFee != null ? formatCurrency(revenueSummary.platformFee) : '—'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Driver Payouts</span>
                      <span className="font-medium">
                        {revenueSummary?.driverPayouts != null ? formatCurrency(revenueSummary.driverPayouts) : '—'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Avg. Order Value</span>
                      <span className="font-medium">
                        {revenueSummary?.avgOrderValue != null ? formatCurrency(revenueSummary.avgOrderValue) : '—'}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Drivers Tab */}
        <TabsContent value="drivers" className="space-y-6 mt-0">
          {driversLoading ? (
            <div className="flex justify-center py-12"><Spinner /></div>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Driver Revenue (Platform vs Rider)</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Driver</TableHead>
                      <TableHead className="text-right">Platform Revenue</TableHead>
                      <TableHead className="text-right">Rider Revenue</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {topDrivers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center py-8 text-muted-foreground">
                          No driver data for selected period
                        </TableCell>
                      </TableRow>
                    ) : (
                      topDrivers.map((driver) => (
                        <TableRow key={driver.id}>
                          <TableCell className="font-medium">{driver.name}</TableCell>
                          <TableCell className="text-right text-emerald-600 font-medium">
                            {formatCurrency(driver.platformRevenue)}
                          </TableCell>
                          <TableCell className="text-right">{formatCurrency(driver.riderRevenue)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Customers Tab */}
        <TabsContent value="customers" className="space-y-6 mt-0">
          {customersLoading ? (
            <div className="flex justify-center py-12"><Spinner /></div>
          ) : (
            <div className="grid gap-6 lg:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Customer Acquisition</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">New Customers</span>
                      <span className="font-medium">{customerReport?.acquisition?.newCustomers?.toLocaleString() ?? '—'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Returning Customers</span>
                      <span className="font-medium">{customerReport?.acquisition?.returningCustomers?.toLocaleString() ?? '—'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Retention Rate</span>
                      <span className="font-medium">
                        {customerReport?.acquisition?.retentionRate != null
                          ? `${customerReport.acquisition.retentionRate.toFixed(1)}%`
                          : '—'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Churn Rate</span>
                      <span className="font-medium text-destructive">
                        {customerReport?.acquisition?.churnRate != null
                          ? `${customerReport.acquisition.churnRate.toFixed(1)}%`
                          : '—'}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Customer Satisfaction</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Average Rating</span>
                      <span className="font-medium">
                        {customerReport?.satisfaction?.averageRating != null
                          ? `${customerReport.satisfaction.averageRating.toFixed(1)} / 5`
                          : '—'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">5 Star Reviews</span>
                      <span className="font-medium">{customerReport?.satisfaction?.fiveStarReviews ?? '—'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Support Tickets</span>
                      <span className="font-medium">{customerReport?.satisfaction?.supportTickets ?? '—'}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
